import React, { useState } from 'react';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';

// Import the logo from the assets folder
import logo from '../assets/ewasko_consulting_cloud_logo.png';

const navigation = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'About', path: '/about' },
  { name: 'Experience', path: '/experience' },
  { name: 'Contact', path: '/contact' },
];

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <AppBar 
      position="sticky" 
      color="default" 
      elevation={2} 
      sx={{ 
        backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
        color: theme.palette.text.primary 
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 2 }}>
          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <img 
              src={logo} 
              alt="Ewasko Consulting Logo" 
              style={{ 
                height: '100px', 
                marginRight: '10px',
                filter: theme.palette.mode === 'dark' ? 'brightness(0.85)' : 'none' 
              }} 
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navigation.map((item) => (
                <Button
                  key={item.name}
                  component={RouterLink}
                  to={item.path}
                  color="inherit"
                  sx={{ 
                    mx: 1,
                    fontWeight: 500,
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  {item.name}
                </Button>
              ))}
              <DarkModeToggle />
              <Button 
                variant="contained" 
                color="primary"
                component={RouterLink}
                to="/contact"
                sx={{ ml: 2 }}
              >
                Get in Touch
              </Button>
            </Box>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DarkModeToggle />
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 240 } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <img 
            src={logo} 
            alt="Ewasko Consulting Logo" 
            style={{ 
              height: '60px', 
              marginRight: '8px',
              filter: theme.palette.mode === 'dark' ? 'brightness(0.85)' : 'none'
            }}
          />
        </Box>
        <List>
          {navigation.map((item) => (
            <ListItem 
              key={item.name} 
              component={RouterLink}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{ 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ListItemText 
                primary={item.name} 
                primaryTypographyProps={{ 
                  fontWeight: 500,
                }}
              />
            </ListItem>
          ))}
          <ListItem sx={{ mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              component={RouterLink}
              to="/contact"
              onClick={handleDrawerToggle}
              fullWidth
            >
              Get in Touch
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Header;